import { GenericEntity } from './common.model';
import { ConfigurationStatus, ConfigurationType, PropertyElement } from './consts';
import { DictionaryItem } from './dictionary.model';
import { FrontierConfiguration } from './frontier.domain';
import { Address, Party } from './party.domain';


export class InsuredProduct extends GenericEntity {
    configuration: GenericProductConfiguration;
    insuranceStart: string;
    insuranceEnd: string;
}

export class InsuredProductProperty extends InsuredProduct {
    configuration: PropertyConfiguration;
}

export class InsuredProductMotor extends InsuredProduct {
    configuration: MotorConfiguration;
    parties?: { [k: number]: Party };
}

export class InsuredProductFrontier extends InsuredProduct {
    configuration: FrontierConfiguration;
}


export enum SummaryType {
    CALCULATION,
    OFFER,
    POLICY,
}

class StaticDocument {
    label: DictionaryItem;
    href: string;
    info: string;
}

export class GenericProductConfiguration extends GenericEntity {
    calculated: boolean;

    messageList: ConfigurationMessage[];

    documentList: StaticDocument[];

    cession: Cession;

    configurationType: ConfigurationType;
    configurationStatus: ConfigurationStatus;

    continuation: ContinuationData;

    insuranceStartDate: string;
    insuranceEndDate: string;

    paymentDefinition: PaymentDefinition;
    premium: number;
}

export class Cession {
    type: DictionaryItem = {code: 'BANK'};
    name: string;
    nip: string;
    regon: string;
    countryOfOrigin: DictionaryItem = {code: 'PL'};
    address: Address = new Address();
}

export enum CessionPartyType {
    BANK = 'BANK',
    LEGAL_ENTITY = 'LEGAL_ENTITY',
}

export class ConfigurationMessage {
    messageType: ConfigurationMessageType;
    message: string;
    field: string;
}

export enum ConfigurationMessageType {
    ERROR = 'ERROR',
    WARNING = 'WARNING',
    INFORMATION = 'INFORMATION'
}

export class PaymentDefinition {
    installments: DictionaryItem;
    deferredPayment: DictionaryItem;
    installmentList: Installment[];
    paymentType: DictionaryItem = new DictionaryItem();
}

export class Installment {
    date: string;
    amount: number;
}

export class GenericRisk {
    selected: boolean;
    sumOfInsurance: number;
    premium: number;
}

export class ContinuationData {
    selected: boolean;
    policyNumber: string;
}

export class MotorConfiguration extends GenericProductConfiguration {
    operationYear: number;
    configurationStep: MotorConfigurationStep;
    driverList: Driver[];
    vehicleDefinition: VehicleDefinition;
    leasing: LeasingDefinition;
    thirdPartyLiability: ThirdPartyLiability;
    autocasco: Autocasco;
    accidentInsurance: AccidentInsurance;
    motorAssistance: MotorAssistance;
    missingClaimHistory: boolean;
    showUfgConfigurationScreen: boolean;
}

export class Driver {
    partyId: number;
    licenseDate: string;
    noLicense: boolean;
    insuranceClaimHistory: InsuranceClaimHistory = new InsuranceClaimHistory();
}

export class InsuranceClaimHistory {
    ufgMode: boolean;
    thirdPartyLiability: InsuranceClaimProductHistory = new InsuranceClaimProductHistory();
    autocasco: InsuranceClaimProductHistory = new InsuranceClaimProductHistory();
}

export class InsuranceClaimProductHistory {
    continuityYears: number;
    claims2y: number;
    claims5y: number;
}

export class VehicleDefinition {
    mode: VehicleDefinitionMode = VehicleDefinitionMode.EUROTAX;
    productionYear: number;
    mileage: number;
    firstRegistrationDate: string;
    licensePlate: string;
    vin: string;
    eurotax: VehicleType = new VehicleType();
    manual: ManualVehicleData = new ManualVehicleData();
    vehicleValue: number;
    vehicleUnusualUsage: VehicleUnusualUsage;
    steeringWheelRightSide: boolean;
}

export enum VehicleDefinitionMode {
    EUROTAX = 'EUROTAX',
    MANUAL = 'MANUAL',
}

export class VehicleType {
    categoryId: number;
    categoryDescription: string;
    makeId: number;
    makeDescription: string;
    modelId: number;
    modelDescription: string;
    typeId: number;
    typeDescription: string;
    bodyType: string;
    bodyTypeItem: DictionaryItem;
    capacity: number;
    powerTax: number;
    horsePower: number;
    watPower: number;
    fuelType: string;
    fuelTypeItem: DictionaryItem;
    emissionStandard: string;
    cylinderCount: number;
    cylinderLayout: string;
    gearBoxType: string;
    gearBoxTypeItem: DictionaryItem;
    gearCount: number;
    doorCount: number;
    seatCount: number;
    totalWeight: number;
    curbWeight: number;
    wheelbase: number;
    fuelConsumption1: number;
    fuelConsumption2: number;
    fuelConsumption3: number;
    currentValue: number;
    mileageCategory: string;
    sport: string;
    vehicleClass: string;
    netValue: string;
}

export class ManualVehicleData {
    type: DictionaryItem;
    make: string;
    model: string;
    bodyType: DictionaryItem;
    doors: DictionaryItem;
    fuelType: DictionaryItem;
    version: string;
    engineCapacity: number;
    enginePower: number;
    loadCapacity: number;
    maximumWeight: number;
}

export class VehicleUnusualUsage {
    usageList: DictionaryItem[];
}

export class LeasingDefinition {
    selected: boolean;
    name: string;
    address: Address;
}

export class ThirdPartyLiability extends GenericRisk {
}

export class Autocasco extends GenericRisk {
    available: boolean;
    claimSettlementMode: DictionaryItem;
}

export class AccidentInsurance extends GenericRisk {
}

export class MotorAssistance extends GenericRisk {
}

export enum MotorConfigurationStep {
    BASIC = 'BASIC',
    CLAIMS = 'CLAIMS',
    DETAILS = 'DETAILS',
    LAST = 'LAST'
}

export class PropertyConfiguration extends GenericProductConfiguration {
    configurationStep: PropertyConfigurationStep;
    propertyType: DictionaryItem;
    address: Address;
    propertyAttributeMap: { [key: string]: boolean };
    elementList: { [key: string]: PropertyElementValue }
    floodRisk: FloodRisk;
    personalLiability: PersonalLiability;
    homeAssistance: HomeAssistance;
}

export class FloodRisk extends GenericRisk {
    claims: DictionaryItem;
}

export class PersonalLiability extends GenericRisk {
    insuredPersonCount: DictionaryItem;
}

export class HomeAssistance extends GenericRisk {
}


export class PropertyElementValue extends GenericRisk {
    element: PropertyElement;
    fire: number;
    firePremium: number;
    theft: number;
    theftPremium: number;
}

export enum PropertyConfigurationStep {
    BASIC = 'BASIC',
    DETAILS = 'DETAILS',
    LAST = 'LAST',
}

export enum PropertyAttribute {
    IS_RENTED = 'IS_RENTED',
    HAS_SAFE_DOOR = 'HAS_SAFE_DOOR',
    HAS_ALARM = 'HAS_ALARM',
    IS_UNDER_PROTECTION = 'IS_UNDER_PROTECTION',
}

export const PropertyFlatElements = [
    {type: PropertyElement.PROPERTY, label: 'OFFER_CONFIG.INSURANCE_SCOPE_FLAT'},
    {type: PropertyElement.GARAGE, label: 'OFFER_CONFIG.OUTBUILDING_GARAGES'},
    {type: PropertyElement.EQUIPMENT, label: 'OFFER_CONFIG.EQUIPMENT'},
    {type: PropertyElement.PERSONAL_BELONGING, label: 'OFFER_CONFIG.PERSONAL_THINGS'},
    {type: PropertyElement.GARDEN_ARCHITECTURE, label: 'OFFER_CONFIG.GARDEN_ARCHITECTURE'},
]

export const PropertyHouseElements = [
    {type: PropertyElement.PROPERTY, label: 'OFFER_CONFIG.INSURANCE_SCOPE_HOUSE'},
    {type: PropertyElement.GARAGE, label: 'OFFER_CONFIG.OUTBUILDING_GARAGES'},
    {type: PropertyElement.EQUIPMENT, label: 'OFFER_CONFIG.EQUIPMENT'},
    {type: PropertyElement.PERSONAL_BELONGING, label: 'OFFER_CONFIG.PERSONAL_THINGS'},
    {type: PropertyElement.GARDEN_ARCHITECTURE, label: 'OFFER_CONFIG.GARDEN_ARCHITECTURE'},
]
